import { useCallback, useRef } from 'react';

import type { useLaunchSummaryQueries } from './useLaunchSummaryQueries';
import type {
    useApplyCycleEvaluationGroupChanges,
    useUpdateCycleEvaluationGroup,
} from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import type { useAmelioTranslations, useNavigate } from '@/hooks';
import type { useError } from '@/hooks/utils/useError';
import type { TranslationKey } from '@/i18n-key';

import { CycleEvaluationGroupSteps } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';
import { getCycleEvaluationGroupStepPath } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { RouteName } from '@/navigation/types';
import { showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { CycleEvaluationGroupStatus } from '@/types/cycle-evaluation/CycleEvaluationGroup';

type AmelioTranslate = ReturnType<typeof useAmelioTranslations>['t'];

export interface UseLaunchSummaryActionsParams {
    group: ReturnType<typeof useLaunchSummaryQueries>['group'];
    cycleEvaluationGroupId: string | undefined;
    updateCycleEvaluationGroup: ReturnType<typeof useUpdateCycleEvaluationGroup>['mutateAsync'];
    applyCycleEvaluationGroupChanges: ReturnType<
        typeof useApplyCycleEvaluationGroupChanges
    >['mutateAsync'];
    isLaunchBlocked: boolean;
    isAlreadyLaunched: boolean;
    /** See `LaunchSummaryProps.hasChangesToApply`. */
    hasChangesToApply: boolean;
    armedCount: number;
    t: AmelioTranslate;
    navigate: ReturnType<typeof useNavigate>;
    handleError: ReturnType<typeof useError>['handleError'];
}

interface LaunchCycleParams {
    groupId: string;
    launchedGroup: NonNullable<UseLaunchSummaryActionsParams['group']>;
}

interface LaunchOutcomeToastParams {
    skippedMilestonesCount: number | null | undefined;
    skippedMessageKey: TranslationKey;
    successTitleKey: TranslationKey;
    successMessageKey: TranslationKey;
    successMessageValues?: Record<string, unknown>;
    t: AmelioTranslate;
}

const showLaunchOutcomeToast = ({
    skippedMilestonesCount,
    skippedMessageKey,
    successTitleKey,
    successMessageKey,
    successMessageValues,
    t,
}: LaunchOutcomeToastParams) => {
    if (skippedMilestonesCount) {
        showWarningToast({
            message: t(skippedMessageKey, { count: skippedMilestonesCount }),
        });

        return;
    }

    showSuccessToast({
        title: t(successTitleKey),
        message: t(successMessageKey, successMessageValues),
    });
};

export const useLaunchSummaryActions = ({
    group,
    cycleEvaluationGroupId,
    updateCycleEvaluationGroup,
    applyCycleEvaluationGroupChanges,
    isLaunchBlocked,
    isAlreadyLaunched,
    hasChangesToApply,
    armedCount,
    t,
    navigate,
    handleError,
}: UseLaunchSummaryActionsParams) => {
    // Not redundant with the disabled button: that guard only commits one render after the click.
    const isSubmittingRef = useRef(false);

    const onBack = useCallback(() => {
        if (!cycleEvaluationGroupId) {
            return;
        }

        navigate({
            path: `${RouteName.CycleEvaluationGroupForm}/${getCycleEvaluationGroupStepPath(
                CycleEvaluationGroupSteps.COMMUNICATIONS,
            )}/${cycleEvaluationGroupId}`,
        });
    }, [navigate, cycleEvaluationGroupId]);

    const applyChanges = useCallback(
        async (groupId: string) => {
            const applied = await applyCycleEvaluationGroupChanges(groupId);

            showLaunchOutcomeToast({
                skippedMilestonesCount: applied.skippedMilestonesCount,
                skippedMessageKey:
                    'cycleEvaluationSchedule.launchSummary.changes.appliedSkippedMilestones',
                successTitleKey: 'cycleEvaluationSchedule.launchSummary.changes.appliedTitle',
                successMessageKey: 'cycleEvaluationSchedule.launchSummary.changes.appliedMessage',
                t,
            });
        },
        [applyCycleEvaluationGroupChanges, t],
    );

    const launchCycle = useCallback(
        async ({ groupId, launchedGroup }: LaunchCycleParams) => {
            const launched = await updateCycleEvaluationGroup({
                id: groupId,
                payload: {
                    name: launchedGroup.name,
                    evaluationPeriodId: launchedGroup.evaluationPeriodId ?? null,
                    evaluationPeriodStart: launchedGroup.evaluationPeriodStart ?? null,
                    evaluationPeriodEnd: launchedGroup.evaluationPeriodEnd ?? null,
                    currentStep: launchedGroup.currentStep,
                    status: CycleEvaluationGroupStatus.ACTIVE,
                    frequency: launchedGroup.frequency ?? undefined,
                },
            });

            showLaunchOutcomeToast({
                skippedMilestonesCount: launched.skippedMilestonesCount,
                // A first launch skips overdue milestones exactly as a reactivation does, from the
                // same server-computed count — a launch-specific key would be a second wording for
                // one behaviour.
                skippedMessageKey: 'cycleEvaluationDashboard.message.reactivateSkippedMilestones',
                successTitleKey: 'cycleEvaluationSchedule.launchSummary.launchReview.successTitle',
                successMessageKey:
                    'cycleEvaluationSchedule.launchSummary.launchReview.successMessage',
                successMessageValues: { count: armedCount },
                t,
            });

            navigate({ path: RouteName.CycleEvaluation });
        },
        [updateCycleEvaluationGroup, armedCount, t, navigate],
    );

    const onLaunch = useCallback(async () => {
        // A blocked launch already lists its reasons on screen; a MISSING group has none, and
        // silence there is a button that does nothing and says nothing.
        if (isLaunchBlocked || isSubmittingRef.current) {
            return;
        }

        // Nothing differs from the launched configuration — or nothing can be compared, when the
        // cycle carries no launch snapshot: either way the post would re-apply what already runs.
        if (isAlreadyLaunched && !hasChangesToApply) {
            return;
        }

        if (!group || !cycleEvaluationGroupId) {
            handleError({});

            return;
        }

        isSubmittingRef.current = true;

        try {
            await (isAlreadyLaunched
                ? applyChanges(cycleEvaluationGroupId)
                : launchCycle({ groupId: cycleEvaluationGroupId, launchedGroup: group }));
        } catch (error) {
            handleError({ error });
        } finally {
            isSubmittingRef.current = false;
        }
    }, [
        isLaunchBlocked,
        group,
        cycleEvaluationGroupId,
        isAlreadyLaunched,
        hasChangesToApply,
        applyChanges,
        launchCycle,
        handleError,
    ]);

    return { onBack, onLaunch };
};
